import { demoDialogTitleSx, demoDialogTextSx } from './dialog-styles'

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'

const AUTO_ADVANCE_KEY = 'demo-auto-advance'

const readAutoAdvance = () => {
  try {
    return localStorage.getItem(AUTO_ADVANCE_KEY) !== 'false'
  } catch {
    return true
  }
}

const DemoStepDialog = ({ step, onContinue }: DemoStepDialogProps) => {
  const { t } = useTranslation()
  const [ automatic, setAutomatic ] = useState(readAutoAdvance)
  const [ remaining, setRemaining ] = useState(10)
  const isLastStep = step === 6

  useEffect(() => {
    if (!automatic) {
      return
    }

    const deadline = Date.now() + 10000
    const timer = window.setInterval(() => {
      const seconds = Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
      setRemaining(seconds)

      if (seconds === 0) {
        window.clearInterval(timer)
        onContinue()
      }
    }, 200)

    return () => window.clearInterval(timer)
  }, [ automatic, onContinue ])

  const changeAutomatic = (checked: boolean) => {
    setAutomatic(checked)
    setRemaining(10)

    try {
      localStorage.setItem(AUTO_ADVANCE_KEY, String(checked))
    } catch {
      // Keep the selected preference for this visit when storage is unavailable.
    }
  }

  const countdownKey = isLastStep ? 'calc__demoPlayer__summaryCountdown' : 'calc__demoPlayer__countdown'
  const waitingKey = isLastStep ? 'calc__demoPlayer__summaryWaiting' : 'calc__demoPlayer__waiting'
  const statusKey = automatic ? countdownKey : waitingKey

  return (
    <div className='demo-step-dialog' data-demo-allowed='player'>
      <div className='demo-dialog' role='dialog' aria-modal='false' aria-labelledby='demo-step-title'>
        <Typography
          id='demo-step-title'
          component='h2'
          sx={demoDialogTitleSx}
        >
          {t(isLastStep ? 'calc__demoPlayer__lastStepCompleted' : 'calc__demoPlayer__stepCompleted', { step: step + 1 })}
        </Typography>
        <div className='demo-dialog__paragraphs'>
          <Typography
            sx={demoDialogTextSx}
          >
            {t(`calc__demoPlayer__stepSummary${step + 1}`)}
          </Typography>
          <Typography
            sx={demoDialogTextSx}
          >
            {t(statusKey, { seconds: remaining })}
          </Typography>
          <FormControlLabel
            sx={{
              alignItems: 'flex-start',
              margin: 0,
              '@media (max-width: 767px)': {
                gap: '8px',
              }
            }}
            control={<Checkbox
              sx={{
                flexShrink: 0,
                '@media (max-width: 767px)': {
                  padding: 0,
                },
              }}
              checked={automatic} onChange={event => changeAutomatic(event.target.checked)} />}
            label={<Typography
              sx={demoDialogTextSx}
            >{t(isLastStep ? 'calc__demoPlayer__automaticSummary' : 'calc__demoPlayer__automatic')}</Typography>}
          />
          <Button
            variant='contained'
            sx={{
              minWidth: 0,
            }}
            onClick={onContinue}
          >
            <Typography variant='button'>{t(isLastStep ? 'calc__demoPlayer__showSummary' : 'calc__demoPlayer__continue')}</Typography>
          </Button>
        </div>
      </div>
    </div>
  )
}

export default DemoStepDialog
